import { FC } from 'react';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components';

import CircularProgress from '@material-ui/core/CircularProgress';
import { forgotPassword } from '../../api/AuthenticationAPI';
import { useNotification } from '../../contexts/notification-context';
import TruckCaddyLogo from '../../assets/images/logo.png';
import Banner from '../../assets/images/publicBanner02.jpg';
import Pattern from '../../assets/images/b01.png';
import OnBoardingTextField from '../../components/OnboardingTextField/OnBoardingTextField';

const Container = styled.div`
  height: 100vh;
  display: flex;

  &.MuiGrid-container {
    flex-wrap: nowrap;
  }
`;
const SideBar = styled.div`
  box-sizing: border-box;
  background-image: url(${Banner});
  background-size: 100% 100%;
  background-repeat: no-repeat;
  flex-grow: 0;
  padding: 75px 85px 50px;
  max-width: 33.333333%;
  flex-basis: 33.333333%;
  @media (max-width: 900px) {
    display: none;
  }
`;
const SideBarContent = styled.div`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
`;
const SideBarTitle = styled.h1`
  max-width: 300px;
  margin: 0;
  text-align: center;
  font-family: Nunito, sans-serif;
  font-weight: 600;
  font-size: 48px;
  letter-spacing: -0.03em;
  color: rgba(255, 255, 255, 0.5);
  line-height: 103.19%
`;
const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

`;
const TitleImage = styled.img`
  height: auto;
  max-width: 100%;
`;
const MainContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  max-width: 66.666667%;
  flex-basis: 66.666667%;
  padding: 75px 25px;

  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    right: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
    background-repeat: no-repeat;
    background-size: auto;
    background-position: right bottom;
    background-image: url(${Pattern});
    background-color: #f6f7fc;
    background-blend-mode: multiply;
    transform: scaleX(-1);
  }

  @media (max-width: 900px) {
    max-width: 100%;
    flex-basis: 100%;
  }
  @media (max-width: 600px) {
    &:before {
      background-image: none;
    }
  }
`;
const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  form {
    width: 100%;
    @media (max-width: 600px) {
      align-items: center;
    }
  }
`;
const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-width: 375px;
  width: 100%;
`;
const Title = styled.span`
  color: #16244A;
  font-family: Cabin, sans-serif;
  font-style: normal;
  font-weight: bold;
  font-size: 48px;
  letter-spacing: -0.015em;
  margin-bottom: 30px;
`;

const Subtitle = styled.span`
  font-family: Roboto, sans-serif;
  font-style: normal;
  font-weight: 300;
  font-size: 19px;
  color: #91a5da;
  margin-bottom: 30px;
`;


const Button = styled.button`
  background: #405daa;
  border-radius: 3px;
  border: none;
  font-family: Nunito, sans-serif;
  color: white;
  font-size: 16px;
  align-items: center;
  text-align: center;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  width: 282px;
  height: 40px;
  margin-top: 56px;
  cursor: pointer;

  :disabled {
    background: #91a5da;
    cursor: auto;
  }
`;

const ForgotPasswordPage: FC = () => {
  const { setNotification } = useNotification();


  // Send a mail to the email address entered to reset the user's password.
  const handleSubmit = (
    values: { email: string },
    { setSubmitting, resetForm }: FormikHelpers<{ email: string }>,
  ): void => {
    const { email } = values;
    forgotPassword(email)
      .then(({ data }) => {
        if (data.success) {
          setNotification({ type: 'success', message: data.msg, open: true });
          resetForm();
        }
      })
      .catch(({ response }) => {
        setNotification({
          type: 'error',
          message: response.data.msg,
          open: true,
        });
      })
      .finally(() => setSubmitting(false));
  };

  // validations object for the forgot pass form
  const forgotPassValidations = Yup.object({
    email: Yup.string().email('Invalid email address').required('Required'),
  });

  return (
    <Container>
      <MainContainer>
        <FormContainer>
          <Title>Forgot Password?</Title>
          <Subtitle>
            Please enter your email address to receive a verification card.
          </Subtitle>
          <Formik
            initialValues={{ email: '' }}
            validationSchema={forgotPassValidations}
            enableReinitialize
            onSubmit={handleSubmit}
          >
            {({ values, isSubmitting }) => (
              <>
                <Form>
                  <FieldsContainer>
                    <OnBoardingTextField name='email' type='text' label='Email address' />
                  </FieldsContainer>
                  {!isSubmitting ? (
                    <Button disabled={!values.email} type='submit'>
                      Send
                    </Button>
                  ) : (
                    <>
                      <div style={{ marginTop: '56px' }} />
                      <CircularProgress disableShrink />
                    </>
                  )}
                </Form>
              </>
            )}
          </Formik>
        </FormContainer>
      </MainContainer>
      <SideBar>
        <SideBarContent>
          <SideBarTitle>The safest way for you</SideBarTitle>
          <LogoContainer>
            <TitleImage src={TruckCaddyLogo} />
          </LogoContainer>
        </SideBarContent>
      </SideBar>
    </Container>
  );
};

export default ForgotPasswordPage;
